/**
 * Project utility functions for data360 live integration
 */

/**
 * Filter project by exact project name match
 * @param {Array} projects - Array of projects from API
 * @param {string} projectName - Project name to search for
 * @returns {Object|null} - Found project or null
 */
export const filterProjectByName = (projects, projectName) => {
  if (!projects || !Array.isArray(projects) || !projectName) {
    return null;
  }
  
  const foundProject = projects.find(project => {
    const nameMatch = project.project_name === projectName || project.name === projectName;
    return nameMatch;
  });
  
  return foundProject || null;
};

/**
 * Filter project by exact project number match
 * @param {Array} projects - Array of projects from API
 * @param {string} projectNumber - Project number to search for
 * @returns {Object|null} - Found project or null
 */
export const filterProjectByNumber = (projects, projectNumber) => {
  if (!projects || !Array.isArray(projects) || !projectNumber) {
    return null;
  }
  
  const foundProject = projects.find(project => {
    const numberMatch = project.project_number === projectNumber || project.projectNumber === projectNumber;
    return numberMatch;
  });
  
  return foundProject || null;
};

/**
 * Get project ID by project number from data360 live
 * @param {Array} projects - Array of projects from API
 * @param {string} projectNumber - Project number to search for
 * @returns {number|null} - Project ID if found, null otherwise
 */
export const getProjectIdByNumber = (projects, projectNumber) => {
  if (!projects || !Array.isArray(projects) || !projectNumber) {
    return null;
  }
  
  const project = filterProjectByNumber(projects, projectNumber);
  return project ? getProjectId(project) : null;
};

/**
 * Get project details by project number from data360 live
 * @param {Array} projects - Array of projects from API
 * @param {string} projectNumber - Project number to search for
 * @returns {Object|null} - Complete project object if found, null otherwise
 */
export const getProjectByNumber = (projects, projectNumber) => {
  if (!projects || !Array.isArray(projects) || !projectNumber) {
    return null;
  }
  
  return filterProjectByNumber(projects, projectNumber);
};

/**
 * Filter project by project ID
 * @param {Array} projects - Array of projects from API
 * @param {string|number} projectId - Project ID to search for
 * @returns {Object|null} - Found project or null
 */
export const filterProjectById = (projects, projectId) => {
  if (!projects || !Array.isArray(projects) || projectId == null) {
    return null;
  }
  
  const foundProject = projects.find(project => {
    const idMatch = project.project_id == projectId || project.id == projectId;
    return idMatch;
  });
  
  return foundProject || null;
};

/**
 * Get project ID from project object
 * @param {Object} project - Project object
 * @returns {number|null} - Project ID or null
 */
export const getProjectId = (project) => {
  if (!project) return null;
  
  return project.project_id || project.id || null;
};

/**
 * Get project name from project object
 * @param {Object} project - Project object
 * @returns {string|null} - Project name or null
 */
export const getProjectName = (project) => {
  if (!project) return null;
  
  return project.project_name || project.name || null;
};

/**
 * Get project number from project object
 * @param {Object} project - Project object
 * @returns {string|null} - Project number or null
 */
export const getProjectNumber = (project) => {
  if (!project) return null;
  
  return project.project_number || project.projectNumber || null;
};

/**
 * Comprehensive project filter - searches by name, number, or ID
 * @param {Array} projects - Array of projects from API
 * @param {string|number} searchValue - Value to search for
 * @returns {Object|null} - Found project or null
 */
export const findProject = (projects, searchValue) => {
  if (!projects || !Array.isArray(projects) || !searchValue) {
    return null;
  }
  
  // Try to find by project ID first (if searchValue is a number)
  if (!isNaN(searchValue)) {
    const byId = filterProjectById(projects, parseInt(searchValue));
    if (byId) {
      return byId;
    }
  }
  
  // Try to find by project number
  const byNumber = filterProjectByNumber(projects, searchValue.toString());
  if (byNumber) {
    return byNumber;
  }
  
  // Try to find by project name
  const byName = filterProjectByName(projects, searchValue.toString());
  if (byName) {
    return byName;
  }
  
  return null;
};

/**
 * Get all unique project names from projects array
 * @param {Array} projects - Array of projects from API
 * @returns {Array} - Array of unique project names
 */
export const getAllProjectNames = (projects) => {
  if (!projects || !Array.isArray(projects)) {
    return [];
  }
  
  const names = projects.map(project => getProjectName(project)).filter(Boolean);
  return [...new Set(names)];
};

/**
 * Get all unique project numbers from projects array
 * @param {Array} projects - Array of projects from API
 * @returns {Array} - Array of unique project numbers
 */
export const getAllProjectNumbers = (projects) => {
  if (!projects || !Array.isArray(projects)) {
    return [];
  }
  
  const numbers = projects.map(project => getProjectNumber(project)).filter(Boolean);
  return [...new Set(numbers)];
};

/**
 * Validate if a project object has required fields
 * @param {Object} project - Project object to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidProject = (project) => {
  if (!project || typeof project !== 'object') {
    return false;
  }
  
  const hasId = getProjectId(project) != null;
  const hasName = getProjectName(project) != null;
  const hasNumber = getProjectNumber(project) != null;
  
  return hasId && hasName && hasNumber;
};

/**
 * Create a standardized project object for consistent usage
 * @param {Object} project - Raw project object from API
 * @returns {Object} - Standardized project object
 */
export const standardizeProject = (project) => {
  if (!isValidProject(project)) {
    return null;
  }
  
  return {
    id: getProjectId(project),
    projectId: getProjectId(project),
    name: getProjectName(project),
    projectName: getProjectName(project),
    number: getProjectNumber(project),
    projectNumber: getProjectNumber(project),
    customerName: project.customer_name || project.customerName || null,
    weldingSystems: project.welding_systems || project.weldingSystems || [],
    pipeThickness: project.pipe_thickness || project.pipeThickness || null,
    pipeDiameter: project.pipe_diameter || project.pipeDiameter || null,
    pipeMaterial: project.pipe_material || project.pipeMaterial || null,
    startDate: project.start_date || project.startDate || null,
    endDate: project.end_date || project.endDate || null,
    userId: project.user_id || project.userId || null,
    numStations: project.num_stations || project.numStations || null,
    weldsPlanned: project.welds_planned || project.weldsPlanned || null,
    weldsCompleted: project.welds_completed || project.weldsCompleted || null,
    reportColumns: project.report_columns || project.reportColumns || {},
    units: project.units || {},
    // Additional fields
    weldXMLErrors: project.weldXMLErrors || null,
    errorBanner: project.errorBanner || null,
    freezeWeldingSystem: project.freezeWeldingSystem || null,
    statusCalculationMethod: project.statusCalculationMethod || null,
    numberOfUnknownWelds: project.numberOfUnknownWelds || null,
    weldAssignmentType: project.weldAssignmentType || null,
    numHeadsIwm: project.num_heads_iwm || project.numHeadsIwm || null
  };
}; 